import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumb from '../components/Breadcrumb';
import { BookOpen, Search, Filter, Tag, Eye, Download, FolderOpen } from 'lucide-react';

const CATEGORIES = [
  { value: '', label: 'Tüm Kategoriler' },
  { value: 'Plan Örneği', label: '📘 Plan Örnekleri' },
  { value: 'Etkinlik', label: '🎨 Etkinlik Bankası' },
  { value: 'Özel Gün Takvimi', label: '📅 Özel Gün Takvimleri' },
  { value: 'Okuma Listesi', label: '📚 Okuma Listeleri' },
  { value: 'Kılavuz', label: '📖 Kılavuzlar' },
  { value: 'Politika', label: '📋 Politika Dokümanları' }
];

const Library = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filteredDocs, setFilteredDocs] = useState([]);

  // Set page title
  useEffect(() => {
    document.title = 'Kaynak Kütüphanesi | İlkadım360 Hub';
  }, []);

  useEffect(() => {
    loadLibrary();
  }, []);

  useEffect(() => {
    filterDocuments();
  }, [documents, searchTerm, selectedCategory]);

  const loadLibrary = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Dokümanları yükle
      const docsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/library/documents`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (docsResponse.ok) {
        const docsData = await docsResponse.json();
        setDocuments(docsData);
      }
      
      // Koleksiyonları yükle
      const collectionsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/library/collections`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (collectionsResponse.ok) {
        const collectionsData = await collectionsResponse.json();
        setCollections(collectionsData);
      }
    } catch (error) {
      console.error('Kütüphane yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = [...documents];
    
    // Kategori filtresi
    if (selectedCategory) {
      filtered = filtered.filter(doc => doc.category === selectedCategory);
    }
    
    // Arama
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.title.toLowerCase().includes(search) ||
        doc.description.toLowerCase().includes(search) ||
        doc.tags.some(tag => tag.toLowerCase().includes(search))
      );
    }
    
    setFilteredDocs(filtered);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Plan Örneği': '📘',
      'Etkinlik': '🎨',
      'Özel Gün Takvimi': '📅',
      'Okuma Listesi': '📚',
      'Kılavuz': '📖',
      'Politika': '📋',
      'Diğer': '📄'
    };
    return icons[category] || '📄';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb 
        items={[
          { label: 'Ana Sayfa', path: '/dashboard' },
          { label: 'Doküman Kütüphanesi', path: null }
        ]} 
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <BookOpen className="text-blue-600" size={40} />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Doküman Kütüphanesi</h1>
            <p className="text-gray-600 mt-1">
              İlkadım360 programına ait onaylı dokümanlar, plan örnekleri ve eğitim materyalleri
            </p>
          </div>
        </div>
      </div>

      {/* Koleksiyonlar */}
      {collections.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FolderOpen size={24} className="text-blue-600" />
            Öne Çıkan Koleksiyonlar
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {collections.slice(0, 3).map((collection) => (
              <div
                key={collection.id}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
              >
                <h3 className="font-semibold text-gray-900 mb-2">{collection.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{collection.description}</p>
                <span className="text-sm text-blue-600 font-medium">
                  {collection.documentIds.length} doküman
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Arama ve Filtreler */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Doküman ara... (başlık, açıklama, etiket)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-500" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
          <span>{filteredDocs.length} doküman listeleniyor</span>
          {(searchTerm || selectedCategory) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('');
              }}
              className="text-blue-600 hover:text-blue-700"
            >
              Filtreleri Temizle
            </button>
          )}
        </div>
      </div>

      {/* Doküman Listesi */}
      {filteredDocs.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <BookOpen className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || selectedCategory ? 'Doküman bulunamadı' : 'Henüz doküman eklenmemiş'}
          </h3>
          <p className="text-gray-500">
            {searchTerm || selectedCategory 
              ? 'Farklı arama terimleri veya filtreler deneyin' 
              : 'YÖMEV ekibi doküman eklediğinde burada görünecektir'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredDocs.map((doc) => (
            <div
              key={doc.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/library/${doc.id}`)}
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{getCategoryIcon(doc.category)}</span>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 hover:text-blue-600">
                          {doc.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            {doc.category}
                          </span>
                          {doc.currentVersion > 0 && (
                            <span className="text-gray-500">v{doc.currentVersion}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-3 line-clamp-2">{doc.description}</p>
                    
                    {doc.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {doc.tags.slice(0, 5).map((tag, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                          >
                            <Tag size={12} />
                            {tag}
                          </span>
                        ))}
                        {doc.tags.length > 5 && (
                          <span className="text-xs text-gray-500">
                            +{doc.tags.length - 5} daha
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Eye size={16} />
                        {doc.viewCount} görüntüleme
                      </span>
                      <span className="flex items-center gap-1">
                        <Download size={16} />
                        {doc.downloadCount} indirme
                      </span>
                      {doc.targetAudience.length > 0 && (
                        <span>👥 {doc.targetAudience.join(', ')}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="ml-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/library/${doc.id}`);
                      }}
                      className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 text-sm font-medium"
                    >
                      Görüntüle
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Library;
